import isNetworkError from 'is-network-error'
import { isTimeoutError } from 'ky'

/** Safari/WebKit's network-failure message for a rejected `fetch()`, optionally
 *  suffixed with a hostname (e.g. `Load failed (forest-api.seekrtech.com)`). */
function isSafariLoadFailed(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    (error.message === 'Load failed' ||
      (error.message.startsWith('Load failed (') &&
        error.message.endsWith(')')))
  )
}

/**
 * True when an error is a connectivity failure (the request timed out or the
 * network dropped) rather than an application/HTTP error.
 *
 * Why this exists instead of calling `isNetworkError` directly: that library
 * recognises Safari's `TypeError: Load failed` only when `error.stack` is
 * `undefined`. That heuristic holds on desktop Safari but NOT in the iOS
 * WKWebView this app runs inside, where the same fetch failure frequently
 * carries a stack. The consequence was genuine network errors slipping past
 * our `isNetworkError` guards and being reported to Sentry as `Load failed`.
 * We match the message directly so the verdict no longer depends on whether a
 * stack happens to be attached.
 */
export function isConnectivityError(error: unknown): boolean {
  return (
    isTimeoutError(error) ||
    (isNetworkError as (e: unknown) => boolean)(error) ||
    isSafariLoadFailed(error)
  )
}
